import React, { useEffect, useState } from "react";
import { ClipInfo, clipInfo, subscribeClipState } from "utils/clipRegistry";
import { ClipMarkSC } from "../../styled";

/**
 * Say, on the row itself, whether this voicing can be played right now.
 *
 * Preparing a clip costs wildly different amounts of time on different machines, and until now
 * that cost was invisible: a translator whose editor stuttered had no way to tell a slow computer
 * from a broken file from a bug, and no way to describe it except "it lags". A stripe per row and
 * a number in its tooltip turn that into something reportable.
 *
 * Its own component, subscribed to one cue, because the row around it is expensive to render and
 * these transitions arrive in bursts across the whole folder.
 */
export const ClipMark: React.FC<{ id: string }> = React.memo(({ id }) => {
    const [info, setInfo] = useState<ClipInfo>(() => clipInfo(id));
    useEffect(() => {
        // Subscribing does not replay: catch up first, or a cue that became ready between the
        // first render and this effect would sit grey for as long as nothing else changed.
        setInfo(clipInfo(id));
        return subscribeClipState((changed, next) => {
            if (changed === id) setInfo(next);
        });
    }, [id]);

    // While it is being prepared the interesting number is how long it has taken SO FAR, which
    // nothing else would push a re-render for.
    const [, forceTick] = useState(0);
    useEffect(() => {
        if (info.state !== "loading") return;
        const timer = window.setInterval(() => forceTick((n) => n + 1), 1000);
        return () => window.clearInterval(timer);
    }, [info.state]);

    return <ClipMarkSC state={info.state} title={describe(info)} />;
});

ClipMark.displayName = "ClipMark";

const seconds = (ms: number) => (ms / 1000).toFixed(1).replace(".", ",");

const describe = (info: ClipInfo): string => {
    switch (info.state) {
        case "ready":
            return info.readyMs
                ? `Озвучка готова (подготовка ${seconds(info.readyMs)} с)`
                : "Озвучка готова";
        case "loading":
            return `Готовится… ${seconds(Date.now() - info.since)} с`;
        case "error":
            return `Не удалось подготовить${info.detail ? `: ${info.detail}` : ""}`;
        case "editing":
            return "Переозвучивается — звук будет заменён";
        default:
            return "Ещё не готовилась (далеко от текущего места)";
    }
};
